import React, { useCallback, useEffect, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { X } from 'lucide-react';
import styles from './TicketLinks.module.css';
import { SEARCH_EVENTS_FOR_LINK, SEARCH_USERS_FOR_LINK } from '../api/supportTicketsQuery';
import { linkVlSupportTicket } from '../../ManageTickets/api/vlListing.api';
import { eventLinkSearchWhere, userLinkSearchWhere } from '../supportTicketUtils';

const GET_LINKED_USER = gql`
  query GetLinkedSupportUser($id: uuid!) {
    user_by_pk(u_id: $id) {
      u_id
      u_full_name
      u_email_id
    }
  }
`;

type Props = {
  ticketId: string;
  userId?: string | null;
  eventId?: string | null;
  eventName?: string | null;
  /** Called after a link changes so the thread and lists reload. */
  onChanged: () => void;
};

type UserHit = { u_id: string; u_full_name?: string | null; u_email_id?: string | null };
type EventHit = { e_id: string; e_name: string; e_date?: string | null };

function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Link a ticket Victory Live started to a user and an event, or take a link away. */
export function TicketLinks({ ticketId, userId, eventId, eventName, onChanged }: Props) {
  const apollo = useApolloClient();
  const [linkedUser, setLinkedUser] = useState<UserHit | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [eventQuery, setEventQuery] = useState('');
  const [userHits, setUserHits] = useState<UserHit[] | null>(null);
  const [eventHits, setEventHits] = useState<EventHit[] | null>(null);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [searchingEvents, setSearchingEvents] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debouncedUser = useDebounced(userQuery);
  const debouncedEvent = useDebounced(eventQuery);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setLinkedUser(null);
      return;
    }
    apollo
      .query({ query: GET_LINKED_USER, variables: { id: userId }, fetchPolicy: 'network-only' })
      .then(({ data }) => !cancelled && setLinkedUser(data?.user_by_pk ?? null))
      .catch(() => !cancelled && setLinkedUser(null));
    return () => {
      cancelled = true;
    };
  }, [apollo, userId]);

  // Results appear as you type (after 2 letters). Each word can match on its own, in any order.
  useEffect(() => {
    let cancelled = false;
    const where = debouncedUser.trim().length >= 2 ? userLinkSearchWhere(debouncedUser) : null;
    if (!where) {
      setUserHits(null);
      return;
    }
    setSearchingUsers(true);
    apollo
      .query({ query: SEARCH_USERS_FOR_LINK, variables: { where }, fetchPolicy: 'network-only' })
      .then(({ data }) => !cancelled && setUserHits(data?.user || []))
      .catch(() => !cancelled && setUserHits([]))
      .finally(() => !cancelled && setSearchingUsers(false));
    return () => {
      cancelled = true;
    };
  }, [apollo, debouncedUser]);

  useEffect(() => {
    let cancelled = false;
    const where = debouncedEvent.trim().length >= 2 ? eventLinkSearchWhere(debouncedEvent) : null;
    if (!where) {
      setEventHits(null);
      return;
    }
    setSearchingEvents(true);
    apollo
      .query({ query: SEARCH_EVENTS_FOR_LINK, variables: { where }, fetchPolicy: 'network-only' })
      .then(({ data }) => !cancelled && setEventHits(data?.events || []))
      .catch(() => !cancelled && setEventHits([]))
      .finally(() => !cancelled && setSearchingEvents(false));
    return () => {
      cancelled = true;
    };
  }, [apollo, debouncedEvent]);

  const link = useCallback(
    async (links: { userId?: string | null; eventId?: string | null }) => {
      setBusy(true);
      setError(null);
      try {
        await linkVlSupportTicket(ticketId, links);
        setUserQuery('');
        setEventQuery('');
        onChanged();
      } catch (err: any) {
        setError(err?.message || "Couldn't link the ticket.");
      } finally {
        setBusy(false);
      }
    },
    [ticketId, onChanged],
  );

  return (
    <div className={styles.links}>
      <div className={styles.row}>
        <span className={styles.label}>User</span>
        {userId ? (
          <span className={styles.linked}>
            {linkedUser?.u_full_name || linkedUser?.u_email_id || 'Linked user'}
            <button type="button" className={styles.clear} onClick={() => void link({ userId: null })} disabled={busy} aria-label="Remove the user link">
              <X size={12} />
            </button>
          </span>
        ) : (
          <div className={styles.search}>
            <input
              className={styles.input}
              placeholder="Search by name or email"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              aria-label="Search users to link"
              disabled={busy}
            />
            {searchingUsers && <div className={styles.hint}>Searching…</div>}
            {!searchingUsers && userHits && userHits.length === 0 && <div className={styles.hint}>No users match "{debouncedUser.trim()}".</div>}
            {userHits && userHits.length > 0 && (
              <ul className={styles.hits}>
                {userHits.map((u) => (
                  <li key={u.u_id}>
                    <button type="button" className={styles.hit} onClick={() => void link({ userId: u.u_id })} disabled={busy}>
                      {u.u_full_name || u.u_email_id}
                      {u.u_full_name && u.u_email_id ? <span className={styles.hitSub}>{u.u_email_id}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className={styles.row}>
        <span className={styles.label}>Event</span>
        {eventId ? (
          <span className={styles.linked}>
            {eventName || 'Linked event'}
            <button type="button" className={styles.clear} onClick={() => void link({ eventId: null })} disabled={busy} aria-label="Remove the event link">
              <X size={12} />
            </button>
          </span>
        ) : (
          <div className={styles.search}>
            <input
              className={styles.input}
              placeholder="Search by event name"
              value={eventQuery}
              onChange={(e) => setEventQuery(e.target.value)}
              aria-label="Search events to link"
              disabled={busy}
            />
            {searchingEvents && <div className={styles.hint}>Searching…</div>}
            {!searchingEvents && eventHits && eventHits.length === 0 && <div className={styles.hint}>No events match "{debouncedEvent.trim()}".</div>}
            {eventHits && eventHits.length > 0 && (
              <ul className={styles.hits}>
                {eventHits.map((ev) => (
                  <li key={ev.e_id}>
                    <button type="button" className={styles.hit} onClick={() => void link({ eventId: ev.e_id })} disabled={busy}>
                      {ev.e_name}
                      {ev.e_date ? <span className={styles.hitSub}>{new Date(ev.e_date).toLocaleDateString()}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}
